// #ux-redesign-start: PasskeyButton (Auth redesign · WebAuthn · Patients)
//
// Implementación 100% funcional de WebAuthn:
//   - Registro: registerBegin → navigator.credentials.create() → registerFinish.
//   - Login:    authBegin     → navigator.credentials.get()    → authFinish.
//
// Backend: PatientsPasskeyController (`/auth/patients/passkey/...`).
// Frontend ↔ Backend: src/services/passkeyService.js.
//
// Tras authFinish, delegamos el post-login al saga `actionPasskeyLogin` (mismo
// enriquecimiento pifs + profile que el login normal de paciente). NO hay
// practices/permissions ni notifications como en Web-Doctors.
//
// Detección de soporte: el botón se oculta si el browser no expone WebAuthn.
// Pre-flight: WebAuthn requiere SECURE CONTEXT (HTTPS válido o localhost) y
// rpId con hostname (no IP).
// ─────────────────────────────────────────────────────────────────────────────

import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { useDispatch } from 'react-redux';
import { Button, toast } from 'components/ds';
import { actionPasskeyLogin } from 'store/actions/usersActions';
import {
  passkeyService,
  bufToB64u,
  bufToB64,
  b64uToBuf,
} from 'services/passkeyService';

// Storage local: guardamos el email del último user que enroló una passkey en
// este browser, para prellenar el campo cuando vuelva a usar la passkey.
// No guardamos el credentialId — el browser lo administra (passkey resident).
const EMAIL_HINT_KEY = 'tsp-passkey-email-hint';

const KeyIcon = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d="M21 2l-2 2m-7.61 7.61a5.5 5.5 0 1 1-7.778 7.778 5.5 5.5 0 0 1 7.777-7.777zm0 0L15.5 7.5m0 0l3 3L22 7l-3-3m-3.5 3.5L19 4" />
  </svg>
);

const PasskeyButton = ({
  label = 'Mit Passkey anmelden',
  registerLabel = 'Passkey registrieren',
  missingEmailMessage = 'Bitte zuerst die E-Mail-Adresse eingeben, dann den Passkey registrieren.',
  successMessage = 'Anmeldung erfolgreich.',
  registeredMessage = 'Passkey registriert. Klicken Sie erneut, um sich anzumelden.',
  cancelledMessage = 'Passkey-Anmeldung abgebrochen.',
  errorMessage = 'Passkey-Anmeldung fehlgeschlagen.',
  getEmail,
  onVerified,
}) => {
  const dispatch = useDispatch();
  const [supported, setSupported] = useState(false);
  const [hasEmailHint, setHasEmailHint] = useState(false);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    const ok =
      typeof window !== 'undefined' &&
      'PublicKeyCredential' in window &&
      typeof navigator !== 'undefined' &&
      'credentials' in navigator;
    setSupported(Boolean(ok));
    if (ok) setHasEmailHint(Boolean(localStorage.getItem(EMAIL_HINT_KEY)));
  }, []);

  if (!supported) return null;

  const preflight = () => {
    if (typeof window === 'undefined') return 'No window context.';
    if (!window.isSecureContext) {
      return 'Insecure context: WebAuthn requires HTTPS (o localhost). Verificá que estés en https:// o en http://localhost.';
    }
    const host = window.location.hostname;
    if (!host) return 'No hostname disponible.';
    if (/^\d+\.\d+\.\d+\.\d+$/.test(host) || host.includes(':')) {
      return `rpId inválido "${host}". WebAuthn no acepta IPs literales — usá un hostname o "localhost".`;
    }
    return null;
  };

  // Decodifica los campos b64u que vienen del backend a ArrayBuffer (lo que
  // espera navigator.credentials.{create,get}).
  const decodeRegisterOptions = (publicKey) => ({
    ...publicKey,
    challenge: b64uToBuf(publicKey.challenge),
    user: {
      ...publicKey.user,
      id: b64uToBuf(publicKey.user.id),
    },
    excludeCredentials: (publicKey.excludeCredentials || []).map((c) => ({
      ...c,
      id: b64uToBuf(c.id),
    })),
  });

  const decodeAuthOptions = (publicKey) => ({
    ...publicKey,
    challenge: b64uToBuf(publicKey.challenge),
    allowCredentials: (publicKey.allowCredentials || []).map((c) => ({
      ...c,
      id: b64uToBuf(c.id),
    })),
  });

  // Post-ceremonia (registro o auth): persiste el hint del email, muestra el
  // toast de éxito y dispara el login. El token ya fue persistido por
  // passkeyService.{register,auth}Finish. El saga enriquece con pifs + profile.
  const finishSession = (login, email) => {
    if (email) localStorage.setItem(EMAIL_HINT_KEY, email);
    setHasEmailHint(true);
    toast.success(successMessage);
    dispatch(actionPasskeyLogin(login));
    if (onVerified) onVerified({ email: email || login.auth_email || login.username });
  };

  const doRegister = async (email) => {
    const begin = await passkeyService.registerBegin({ email });
    if (!begin.ok) {
      throw new Error(begin.data || `register/begin ${begin.status}`);
    }
    const { nonce, publicKey } = begin.data;

    const cred = await navigator.credentials.create({
      publicKey: decodeRegisterOptions(publicKey),
    });
    if (!cred) throw new Error('No credential returned');

    // Browser modernos exponen getPublicKey() (Chrome 91+, FF 99+, Safari 16+).
    // Devuelve un ArrayBuffer SPKI que verificamos directamente con `crypto.verify`
    // en el backend — sin parsear CBOR del attestationObject.
    const spki = cred.response.getPublicKey
      ? cred.response.getPublicKey()
      : null;
    const algorithm = cred.response.getPublicKeyAlgorithm
      ? cred.response.getPublicKeyAlgorithm()
      : -7;

    if (!spki) {
      throw new Error(
        'Este browser no soporta getPublicKey() — usá Chrome 91+, Firefox 99+ o Safari 16+'
      );
    }

    const body = {
      nonce,
      credentialId: bufToB64u(cred.rawId),
      publicKey: bufToB64(spki),
      algorithm,
      clientDataJSON: bufToB64u(cred.response.clientDataJSON),
      deviceName: navigator.userAgent || 'Browser',
    };

    const finish = await passkeyService.registerFinish(body);
    if (!finish.ok) {
      throw new Error(finish.data || `register/finish ${finish.status}`);
    }

    // #ux-redesign: enrolar + entrar en un solo clic. registerFinish ya emitió
    // la sesión (token persistido por passkeyService), así que logueamos directo
    // sin pedir un segundo clic ni una segunda ceremonia WebAuthn.
    finishSession(finish.data, email);
  };

  const doAuth = async (email) => {
    const begin = await passkeyService.authBegin({ email });
    if (!begin.ok) {
      throw new Error(begin.data || `auth/begin ${begin.status}`);
    }
    const { nonce, publicKey } = begin.data;

    const cred = await navigator.credentials.get({
      publicKey: decodeAuthOptions(publicKey),
    });
    if (!cred) throw new Error('No assertion returned');

    const body = {
      nonce,
      credentialId: bufToB64u(cred.rawId),
      clientDataJSON: bufToB64u(cred.response.clientDataJSON),
      authenticatorData: bufToB64u(cred.response.authenticatorData),
      signature: bufToB64u(cred.response.signature),
    };

    const finish = await passkeyService.authFinish(body);
    if (!finish.ok) {
      throw new Error(finish.data || `auth/finish ${finish.status}`);
    }

    finishSession(finish.data, email);
  };

  const handlePasskey = async () => {
    const blocker = preflight();
    if (blocker) {
      // eslint-disable-next-line no-console
      console.warn('[PasskeyButton] preflight blocked:', blocker);
      toast.error(blocker);
      return;
    }

    setBusy(true);
    const startedAt = Date.now();
    try {
      // Email: prioridad form > storage hint. Si tampoco hay → si el botón
      // muestra "registrar" pedimos email; si muestra "anmelden" dejamos al
      // backend ofrecer discoverable credentials.
      const formEmail = (getEmail && getEmail() || '').trim().toLowerCase();
      const hintEmail = (localStorage.getItem(EMAIL_HINT_KEY) || '').trim().toLowerCase();
      const email = formEmail || hintEmail;

      if (hasEmailHint) {
        // Flow auth: tenemos email guardado del enrol previo.
        await doAuth(email);
      } else {
        // Flow register: requerimos email del form.
        if (!email) {
          toast.warning(missingEmailMessage);
          return;
        }
        await doRegister(email);
      }
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error('[PasskeyButton] error', err);
      const code = err && err.name;
      const detail = (err && err.message) || code || 'unknown';
      const elapsed = Date.now() - startedAt;

      if (code === 'NotAllowedError' || code === 'AbortError') {
        // NotAllowedError es ambiguo en WebAuthn: cubre cancelación del user
        // y bloqueo silencioso del browser por TLS no confiable (cert
        // self-signed, expirado, etc.). Heurística: si tardó < 250ms, no abrió prompt.
        if (elapsed < 250) {
          toast.error(
            `${errorMessage} — el navegador bloqueó la ceremonia sin abrir el prompt. ` +
            'Causa más probable: certificado TLS no confiable (self-signed o expirado).'
          );
        } else {
          toast.info(cancelledMessage);
        }
      } else if (code === 'InvalidStateError') {
        toast.info(registeredMessage);
      } else if (code === 'SecurityError') {
        toast.error(`${errorMessage} (SecurityError): ${detail}`);
      } else if (code === 'NotSupportedError') {
        toast.error(`${errorMessage} (algoritmo no soportado): ${detail}`);
      } else {
        toast.error(`${errorMessage} [${code || 'Error'}]: ${detail}`);
      }
    } finally {
      setBusy(false);
    }
  };

  return (
    <Button
      type="button"
      variant="secondary"
      size="lg"
      fullWidth
      onClick={handlePasskey}
      loading={busy}
      iconLeft={<KeyIcon />}
    >
      {hasEmailHint ? label : registerLabel}
    </Button>
  );
};

PasskeyButton.propTypes = {
  label: PropTypes.node,
  registerLabel: PropTypes.node,
  missingEmailMessage: PropTypes.string,
  successMessage: PropTypes.string,
  registeredMessage: PropTypes.string,
  cancelledMessage: PropTypes.string,
  errorMessage: PropTypes.string,
  getEmail: PropTypes.func,
  onVerified: PropTypes.func,
};

export default PasskeyButton;
// #ux-redesign-end
